import { useRequest } from 'ahooks';
import {
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  Spin,
  Button,
  message,
  Carousel,
  Drawer,
  Radio,
  Col,
  Row,
} from 'antd';
import {
  companyMediaController,
  CompanyMediaTypeEnum,
  Long,
  oSSController,
} from '../../api';
import { useCompany } from '../../hooks/company';
import VideoPreviewWithThumbnail from '../../components/VideoPreviewWithThumbnail';
import { DeleteOutline } from 'antd-mobile-icons';
import useScreenSize from '../../hooks/useScreenSize';

const refreshContext = createContext<{
  refresh: () => void;
}>({ refresh() {} });

const VideoPreview = ({ ossKey }: { ossKey: string }) => {
  const { id: companyId } = useCompany();
  const { data: imageUrl, loading } = useRequest(
    () => {
      return oSSController.getOSSUrl(
        companyId,
        ossKey,
        undefined,
        'video/snapshot,t_17000,f_jpg,w_0,h_0',
      );
    },
    {
      refreshDeps: [companyId, ossKey],
    },
  );
  return (
    <Spin spinning={loading}>
      <VideoPreviewWithThumbnail ossKey={ossKey} thumbnailUrl={imageUrl} />
    </Spin>
  );
};

const DeleteMedia = ({ id }: { id: Long }) => {
  const { id: companyId } = useCompany();
  const { refresh } = useContext(refreshContext);
  const { run, loading } = useRequest(
    () => {
      return companyMediaController.deleteMedia(companyId, id);
    },
    {
      manual: true,
      onError: (err) => {
        message.error(err.message);
      },
      onSuccess: () => {
        message.success('删除成功');
        refresh();
      },
    },
  );

  return (
    <Button
      icon={<DeleteOutline />}
      onClick={() => run()}
      loading={loading}
      size="small"
      danger
    />
  );
};

const DeleteButton = ({ id }: { id: Long }) => {
  const { hasPermission } = useCompany();

  const canEdit = useMemo(() => {
    return hasPermission('page:media:editor');
  }, [hasPermission]);

  if (!canEdit) {
    return null;
  }
  return <DeleteMedia id={id} />;
};

export default ({
  category,
  companyId,
  onClose,
}: {
  category: string;
  companyId: Long;
  onClose: () => void;
}) => {
  const { width, height } = useScreenSize();
  const [type, setType] = useState<CompanyMediaTypeEnum>(
    CompanyMediaTypeEnum.PHOTO,
  );

  const { data, loading, refresh } = useRequest(
    () => {
      return companyMediaController
        .listByCategory(companyId, category)
        .then((result) => {
          return {
            video: result.filter(
              ({ type }) => type === CompanyMediaTypeEnum.VIDEO,
            ),
            image: result.filter(
              ({ type }) => type === CompanyMediaTypeEnum.PHOTO,
            ),
          };
        })
        .then((result) => {
          return Promise.all([
            result.image.length > 0
              ? oSSController
                  .getOSSUrlBatch(
                    companyId,
                    result.image.map((item) => item.ossKey),
                    undefined,
                    `image/resize,h_${height},limit_0`,
                  )
                  .then((urls) => {
                    return urls.map((url, index) => {
                      return {
                        url,
                        id: result.image[index].id,
                      };
                    });
                  })
              : Promise.resolve([]),
            Promise.resolve(result.video.length > 0 ? result.video : []),
          ]).then(([imageUrls, videoUrls]) => {
            return {
              image: imageUrls,
              video: videoUrls,
            };
          });
        });
    },
    {
      refreshDeps: [open, companyId, category],
    },
  );

  return (
    <refreshContext.Provider value={{ refresh: refresh }}>
      <Drawer
        open={!!category}
        onClose={onClose}
        placement="bottom"
        height={height - 40}
        closable={!loading}
        bodyStyle={{ padding: 0 }}
        extra={
          <Radio.Group
            value={type === CompanyMediaTypeEnum.PHOTO ? 'photo' : 'video'}
            onChange={(e) => {
              if (e.target.value === 'photo') {
                setType(CompanyMediaTypeEnum.PHOTO);
              } else {
                setType(CompanyMediaTypeEnum.VIDEO);
              }
            }}
          >
            <Radio.Button
              value={'photo'}
              disabled={data?.image.length === 0 || loading}
            >
              照片
            </Radio.Button>
            <Radio.Button
              value={'video'}
              disabled={data?.video.length === 0 || loading}
            >
              视频
            </Radio.Button>
          </Radio.Group>
        }
      >
        <Spin
          spinning={loading}
          style={{ width: '100%', height: 'calc(100vh - 200px)' }}
        >
          {type === CompanyMediaTypeEnum.PHOTO && (
            <div
              style={{ background: 'rgba(0, 0, 0, 0.9)', padding: '0 20px' }}
            >
              <Carousel dotPosition="bottom">
                {data?.image.map((item) => {
                  return (
                    <div
                      key={item.id}
                      style={{
                        width: '100%',
                        height: '100%',
                      }}
                    >
                      <DeleteButton id={item.id!} />
                      <img
                        src={item.url}
                        style={{
                          height: height - 190,
                          width: 'auto',
                          objectFit: 'contain',
                          margin: '25px auto',
                        }}
                      />
                    </div>
                  );
                })}
              </Carousel>
            </div>
          )}
          {type === CompanyMediaTypeEnum.VIDEO && (
            <Row>
              {data?.video.map((item) => {
                return (
                  <Col span={8} key={item.id}>
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        padding: '10px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <DeleteButton id={item.id!} />
                      <VideoPreview ossKey={item.ossKey} key={item.id} />
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
        </Spin>
      </Drawer>
    </refreshContext.Provider>
  );
};
